'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgBenefitFill, SvgDonationFill, SvgHomeHeartFill, SvgMyFill, SvgStore } from '@common/icon';
import { cn } from '@core/utils';
import { useLinkStatus } from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_ROUTES, MODULE_ROUTES, ROUTES, SELECTOR, SUB_DOMAIN, WEL_MARKET_ROUTES } from '@/constants';
import { isReachableOnToss } from '@/constants/toss';
import { useHaptic } from '@/hooks/useHaptic';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { scrollToPageTop } from '@/utils';
import { gtmDatasets } from '@/utils/analytics/gtm';
type NavItem = {
  name: string;
  href: string;
  icon: typeof SvgHomeHeartFill;
  hasNotificationDot: boolean;
};
const WELLO_NAVIGATION_ITEMS: readonly NavItem[] = [{
  name: '홈',
  href: ROUTES.HOME.pathname,
  icon: SvgHomeHeartFill,
  hasNotificationDot: false
}, {
  name: '고향사랑기부',
  href: DONATION_ROUTES.HOME.pathname,
  icon: SvgDonationFill,
  hasNotificationDot: false
}, {
  name: '웰마켙',
  href: WEL_MARKET_ROUTES.HOME.pathname,
  icon: SvgStore,
  hasNotificationDot: false
}, {
  name: '혜택',
  href: ROUTES.BENEFIT.pathname,
  icon: SvgBenefitFill,
  hasNotificationDot: false
}, {
  name: '마이페이지',
  href: ROUTES.MY.pathname,
  icon: SvgMyFill,
  hasNotificationDot: false
}];
type GlobalNavigationLinkContentProps = Pick<NavItem, 'name' | 'icon' | 'hasNotificationDot'> & {
  isActive: boolean;
};
const GlobalNavigationLinkContent = t0 => {
  const $ = _c(27);
  const {
    name,
    icon: Icon,
    hasNotificationDot,
    isActive
  } = t0;
  const {
    pending
  } = useLinkStatus();
  const iconContainerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== pending) {
    t1 = () => {
      if (!pending) {
        return;
      }
      const linkElement = iconContainerRef.current?.closest("a");
      if (!linkElement) {
        return;
      }
      const preventRepeatedNavigation = _temp;
      linkElement.setAttribute("aria-busy", "true");
      linkElement.setAttribute("aria-disabled", "true");
      linkElement.addEventListener("click", preventRepeatedNavigation, true);
      return () => {
        linkElement.removeAttribute("aria-busy");
        linkElement.removeAttribute("aria-disabled");
        linkElement.removeEventListener("click", preventRepeatedNavigation, true);
      };
    };
    t2 = [pending];
    $[0] = pending;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== pending) {
    t3 = cn("relative transition-opacity", {
      "animate-pulse opacity-60 motion-reduce:animate-none": pending
    });
    $[3] = pending;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = isActive || pending;
  let t5;
  if ($[5] !== t4) {
    t5 = cn("size-28 text-Gray-400", {
      "text-Gray-black": t4
    });
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== Icon || $[8] !== t5) {
    t6 = <Icon className={t5} />;
    $[7] = Icon;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== hasNotificationDot) {
    t7 = hasNotificationDot ? <><span className="sr-only">새 소식 있음</span><div aria-hidden="true" className="absolute -right-4 -top-2 size-6 rounded-pill bg-R-500" /></> : null;
    $[10] = hasNotificationDot;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t3 || $[13] !== t6 || $[14] !== t7) {
    t8 = <div ref={iconContainerRef} className={t3}>{t6}{t7}</div>;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== pending) {
    t9 = cn("transition-colors", {
      "font-700 text-Gray-900": pending
    });
    $[16] = pending;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== name || $[19] !== t9) {
    t10 = <span className={t9}>{name}</span>;
    $[18] = name;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = pending ? "\uD398\uC774\uC9C0 \uC774\uB3D9 \uC911" : null;
  let t12;
  if ($[21] !== t11) {
    t12 = <span aria-live="polite" className="sr-only" role="status">{t11}</span>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12 || $[25] !== t8) {
    t13 = <>{t8}{t10}{t12}</>;
    $[23] = t10;
    $[24] = t12;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
};
export const GlobalNavigation = () => {
  const $ = _c(21);
  const pathname = usePathname();
  const isToss = useDeviceInfoStore(_temp2);
  const {
    triggerHaptic
  } = useHaptic();
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== isToss || $[1] !== pathname || $[2] !== triggerHaptic) {
    let t5;
    if ($[8] !== isToss) {
      t5 = item => !isToss || isReachableOnToss(item.href);
      $[8] = isToss;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    const navigationItems = WELLO_NAVIGATION_ITEMS.filter(t5);
    t2 = "\uBA54\uC778 \uB0B4\uBE44\uAC8C\uC774\uC158";
    t3 = "flex w-full justify-center";
    t4 = SELECTOR.GLOBAL_NAVIGATION;
    t0 = "flex h-70 w-full items-center justify-around px-22";
    let t6;
    if ($[10] !== pathname || $[11] !== triggerHaptic) {
      t6 = item_0 => {
        const {
          name,
          href,
          icon,
          hasNotificationDot
        } = item_0;
        const clickFnbDataset = gtmDatasets.clickFnb({
          menu_name: name
        });
        const isActive = href === ROUTES.HOME.pathname ? pathname === ROUTES.HOME.pathname || pathname === MODULE_ROUTES.KB_HOME.pathname : pathname === href || pathname.startsWith(`${href}/`);
        const isCurrentPage = pathname === href;
        return <li key={name} className="flex-1"><CustomLink aria-current={isCurrentPage ? "page" : undefined} {...clickFnbDataset} className={cn("relative flex-col gap-5 whitespace-nowrap rounded-xxsmall font-500 text-Gray-550 transition-colors flex-center typo-caption1", {
            "text-Gray-900 font-700 cursor-default": isActive
          })} href={href} prefetch={true} onClick={event => {
            if (isCurrentPage) {
              event.preventDefault();
              scrollToPageTop();
              return;
            }
            triggerHaptic();
          }}><GlobalNavigationLinkContent hasNotificationDot={hasNotificationDot} icon={icon} isActive={isActive} name={name} /></CustomLink></li>;
      };
      $[10] = pathname;
      $[11] = triggerHaptic;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t1 = navigationItems.map(t6);
    $[0] = isToss;
    $[1] = pathname;
    $[2] = triggerHaptic;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[13] !== t0 || $[14] !== t1) {
    t5 = <ul className={t0}>{t1}</ul>;
    $[13] = t0;
    $[14] = t1;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <nav aria-label={t2} className={t3} id={t4}>{t5}</nav>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
};
function _temp(event) {
  event.preventDefault();
  event.stopPropagation();
}
function _temp2(state) {
  return state.subDomain === SUB_DOMAIN.TOSS;
}